import type { NewProgram, UnitsPreference } from "@heraion/shared";

import { asyncStorageResourceCache } from "../Logger/state/asyncListCache";
import type { PersistedSnapshot } from "./types";

// The editor's draft ON THE DEVICE (#336), one blob per program or plan, on
// the Logger's ResourceCache adapter (AsyncStorage / localStorage).
// ⛔ The account lives in the KEY (`scope`, the host's per-account prefix):
// no scope, no draft. So does the author's hat: the coach library and the
// athlete's own editor each have their own «new» slot. Fields: `value` the
// edits; `baseline` the server's copy they differ from (the resume compares
// the server's current content against it); `unit` the cells' unit;
// `serverId` an id already written; `mintedId` the id a create sends, kept so
// a resumed create meets its own row instead of making a second one.
// Bump DRAFT_VERSION when the Draft shape changes incompatibly.

const DRAFT_VERSION = 1;

export type EditorDraftBlob = {
  unit: UnitsPreference;
  value: PersistedSnapshot;
  baseline: PersistedSnapshot;
  serverId: string | null;
  mintedId: string | null;
};

export type EditorDraftKind = "program" | "plan";
export type EditorDraftRole = NonNullable<NewProgram["authoredAs"]>;

export const editorDraftKey = (
  scope: string,
  kind: EditorDraftKind,
  role: EditorDraftRole,
  id: string | null,
): string => `${scope}.${kind}.${role}.${id ?? "new"}`;

const isSnapshot = (v: unknown): v is PersistedSnapshot => {
  if (v === null || typeof v !== "object") return false;
  const s = v as { draft?: { sessions?: unknown }; scheduledFor?: unknown };
  return (
    typeof s.scheduledFor === "string" &&
    s.draft !== null &&
    typeof s.draft === "object" &&
    Array.isArray(s.draft.sessions)
  );
};

const parse = (raw: unknown): EditorDraftBlob | null => {
  if (raw === null || typeof raw !== "object") return null;
  const b = raw as Partial<EditorDraftBlob>;
  if (b.unit !== "kg" && b.unit !== "lb") return null;
  if (!isSnapshot(b.value) || !isSnapshot(b.baseline)) return null;
  return {
    unit: b.unit,
    value: b.value,
    baseline: b.baseline,
    serverId: typeof b.serverId === "string" ? b.serverId : null,
    mintedId: typeof b.mintedId === "string" ? b.mintedId : null,
  };
};

const cacheFor = (key: string) =>
  asyncStorageResourceCache<EditorDraftBlob>(key, { version: DRAFT_VERSION });

export const editorDraftStore = {
  /** The stored draft and when it was written, or null (none, or unreadable). */
  read: async (
    key: string,
  ): Promise<{ blob: EditorDraftBlob; savedAtMs: number | null } | null> => {
    const entry = await cacheFor(key).readEntry();
    const blob = entry ? parse(entry.value) : null;
    return blob ? { blob, savedAtMs: entry!.cachedAtMs } : null;
  },
  write: (key: string, blob: EditorDraftBlob): Promise<void> =>
    cacheFor(key).write(blob),
  clear: (key: string): Promise<void> => cacheFor(key).clear(),
};
